import { SlotRendering } from '@frontend/common-types';
import { TextInput } from '@frontend/components/basic-forms';
import { AsyncComponent, DefaultPrimaryButton } from '@frontend/components/common';
import { Constraint } from '@frontend/constraint/types';
import { CommonMessage } from '@frontend/lang';
import { SpotRenderingClient } from '@frontend/spot/api';
import { lazy } from 'react';
import { FormattedMessage } from 'react-intl';

import { formatConstraintData } from '../../../constraint/modal/constraint-detail-sections';
import { SpotLayout } from '../../../spot-rendering';
import { getSlotLabel } from '../../../spot-rendering/utils';
import { AddToSlotModalProps, useAddToSlotModal } from './add-to-slot-modal.controller';

const SpotSelectV2 = lazy(() => import('@frontend/spot/select').then((m) => ({ default: m.SpotSelectV2 })));

export const AddToSlotModal = (props: AddToSlotModalProps) => {
    const viewProps = useAddToSlotModal(props);

    return (
        <div className='flex h-[min(860px,calc(100vh-8rem))] max-h-[calc(100vh-8rem)] w-full max-w-full min-w-0 flex-col gap-5 overflow-hidden p-6'>
            <span className='text-foreground-default shrink-0 text-xl font-semibold'>
                <FormattedMessage
                    id='add-to-slot-modal.title'
                    description='Title of the modal used to add a product, kit, or equipment constraint to a SLOT'
                    defaultMessage='Add to SLOT'
                />
            </span>
            <div className='flex shrink-0 flex-col gap-4'>
                <TextInput
                    label={CommonMessage.OBJECTS.COMMON_FIELDS.NAME}
                    value={viewProps.name}
                    onChange={viewProps.setName}
                    required
                />
            </div>
            <AsyncComponent>
                <div className='flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-hidden'>
                    <SpotSelectV2
                        label={CommonMessage.OBJECTS.SPOT.DEFAULT}
                        value={viewProps.spotId}
                        onChangeObject={viewProps.handleSpotChange}
                        required
                    />
                    <div
                        aria-hidden={!viewProps.hasExistingConstraints && !viewProps.hasOtherConstraintsOnSpot}
                        className={`text-foreground-muted flex h-10 shrink-0 flex-wrap items-center gap-x-4 gap-y-1 overflow-hidden text-sm transition-opacity ${
                            viewProps.hasExistingConstraints || viewProps.hasOtherConstraintsOnSpot ? 'opacity-100' : 'pointer-events-none opacity-0'
                        }`}>
                        {viewProps.hasExistingConstraints && (
                            <span className='flex min-w-0 items-center gap-2'>
                                <span className='bg-background-surface ring-brand-secondary size-3 shrink-0 rounded-full ring-2' />
                                <span className='min-w-0 truncate'>
                                    <FormattedMessage
                                        id='applications.modals.add-to-slot-modal.existing-constraints-legend'
                                        description='Legend shown in the add to SLOT modal explaining the marker for SLOTs that already have a constraint for the selected product, kit, or equipment'
                                        defaultMessage='Marked SLOTs already have a constraint for this item'
                                    />
                                </span>
                            </span>
                        )}
                        {viewProps.hasOtherConstraintsOnSpot && (
                            <span className='flex min-w-0 items-center gap-2'>
                                <span className='bg-status-warning size-3 shrink-0 rounded-full' />
                                <span className='min-w-0 truncate'>
                                    <FormattedMessage
                                        id='applications.modals.add-to-slot-modal.other-constraints-legend'
                                        description='Legend shown in the add to SLOT modal explaining the marker for SLOTs that have constraints unrelated to the selected product, kit, or equipment'
                                        defaultMessage='Marked SLOTs have other constraints'
                                    />
                                </span>
                            </span>
                        )}
                    </div>
                    <div
                        aria-hidden={!viewProps.hasExistingConstraints && !viewProps.hasOtherConstraintsOnSpot}
                        className={`text-foreground-muted flex h-5 shrink-0 items-center gap-2 overflow-hidden text-sm transition-opacity ${
                            viewProps.hasExistingConstraints || viewProps.hasOtherConstraintsOnSpot ? 'opacity-100' : 'pointer-events-none opacity-0'
                        }`}>
                        <span className='min-w-0 truncate'>
                            <FormattedMessage
                                id='applications.modals.add-to-slot-modal.constraints-hover-hint'
                                description='Hint shown in the add to SLOT modal explaining that SLOT constraint details are available by hovering over the SLOT'
                                defaultMessage='Hover a SLOT to inspect its constraints'
                            />
                        </span>
                    </div>
                    {viewProps.selectedSpot && (
                        <div className='border-border-default bg-background-default min-h-0 min-w-0 flex-1 overflow-hidden rounded-xl border'>
                            <SpotLayout
                                key={viewProps.selectedSpot.id}
                                spot={viewProps.selectedSpot}
                                fetchSpotData={(arg) => SpotRenderingClient.fetchSpotRendering(viewProps.currentAccount, viewProps.selectedSpot!.id, arg)}
                                fetchModuleData={SpotRenderingClient.fetchSpotModuleRendering}
                                onSlotClick={viewProps.handleSlotClick}
                                enableZoom
                                enableZoomControls
                                styles={{ getSlotColor: viewProps.getSlotColor, isSlotDisabled: viewProps.isSlotDisabled }}
                                slot={{
                                    renderElement: (slot) =>
                                        renderSlotElement(slot, viewProps.hasExistingConstraint(slot), viewProps.hasOtherConstraints(slot)),
                                    hoverElement: (slot) => renderSlotHoverElement(viewProps.hasExistingConstraint(slot), viewProps.getOtherConstraints(slot))
                                }}
                            />
                        </div>
                    )}
                    <div
                        aria-hidden={!viewProps.slotName}
                        className={`border-border-default bg-background-surface flex h-14 shrink-0 items-center justify-between gap-3 rounded-xl border px-4 transition-opacity ${
                            viewProps.slotName ? 'opacity-100' : 'pointer-events-none opacity-0'
                        }`}>
                        <span className='text-foreground-muted shrink-0 text-sm'>
                            <FormattedMessage
                                id='applications.modals.add-to-slot-modal.selected-slot'
                                description='Label shown before the selected SLOT name in the add to SLOT modal'
                                defaultMessage='Selected SLOT'
                            />
                        </span>
                        <span className='text-foreground-default min-w-0 truncate text-sm font-semibold'>{viewProps.slotName}</span>
                    </div>
                </div>
            </AsyncComponent>
            <div className='flex shrink-0 justify-end'>
                <DefaultPrimaryButton
                    text={CommonMessage.BUTTONS.SUBMIT}
                    onClick={viewProps.handleSubmit}
                    disabled={viewProps.submitDisabled}
                    isLoading={viewProps.isSubmitting}
                />
            </div>
        </div>
    );
};

const renderSlotElement = (slot: SlotRendering, hasExistingConstraint: boolean, hasOtherConstraints: boolean) => (
    <>
        <div className='text-background-surface pointer-events-none absolute bottom-0 left-0 pb-1 pl-2 text-xs font-semibold select-none'>
            {getSlotLabel(slot)}
        </div>
        {hasExistingConstraint && (
            <span className='bg-background-surface ring-brand-secondary pointer-events-none absolute top-1 right-1 size-3 rounded-full ring-2' />
        )}
        {hasOtherConstraints && <span className='bg-status-warning pointer-events-none absolute right-1 bottom-1 size-2.5 rounded-full' />}
    </>
);

const renderSlotHoverElement = (hasExistingConstraint: boolean, otherConstraints: Constraint[]) =>
    hasExistingConstraint || otherConstraints.length > 0 ? (
        <>
            {hasExistingConstraint && (
                <div className='text-foreground-subtle text-xs'>
                    <FormattedMessage
                        id='applications.modals.add-to-slot-modal.slot-has-current-constraint'
                        description='Message shown in the SLOT hover popover when the SLOT already has a constraint for the product, kit, or equipment being added'
                        defaultMessage='This SLOT already has a constraint for this item.'
                    />
                </div>
            )}
            {otherConstraints.length > 0 && (
                <div className='flex max-w-72 flex-col gap-2'>
                    <span className='text-foreground-default text-xs font-semibold'>
                        <FormattedMessage
                            id='applications.modals.add-to-slot-modal.other-constraints-heading'
                            description='Heading shown in the SLOT hover popover above constraints that are unrelated to the selected product, kit, or equipment'
                            defaultMessage='Other constraints'
                        />
                    </span>
                    <div className='flex flex-col gap-1'>
                        {otherConstraints.slice(0, 4).map((constraint) => (
                            <div
                                key={constraint.id}
                                className='text-foreground-subtle flex min-w-0 gap-1 text-xs'>
                                <span className='shrink-0'>{formatConstraintType(constraint.type)}:</span>
                                <div className='min-w-0 truncate'>{formatConstraintData(constraint.data, constraint.type)}</div>
                            </div>
                        ))}
                        {otherConstraints.length > 4 && (
                            <span className='text-foreground-subtle text-xs'>
                                <FormattedMessage
                                    id='applications.modals.add-to-slot-modal.more-other-constraints'
                                    description='Message shown in the SLOT hover popover when only the first few unrelated constraints are displayed'
                                    defaultMessage='+{count} more'
                                    values={{ count: otherConstraints.length - 4 }}
                                />
                            </span>
                        )}
                    </div>
                </div>
            )}
        </>
    ) : null;

const formatConstraintType = (type: string) => (type.charAt(0).toUpperCase() + type.slice(1)).replace(/_/g, ' ');
